import React, { useMemo } from 'react';
import { useGameStore } from '../../store/game-store.js';
import PathVisualization from './PathVisualization.jsx';
import { recordGame } from '../../lib/storage.js';
import { getShareUrl } from '../../lib/url-state.js';
import './WinPanel.css';

export default function WinPanel() {
  const {
    winResult, firstWin, isNewBest, won, targets,
    stats, winPanelState, setWinPanelState,
    gameController,
  } = useGameStore();

  if (!won || !winResult) return null;

  const filmCount = winResult.uniqueMediaCount;

  // Post-game insights
  const insights = useMemo(() => {
    if (!winResult || !gameController) return null;
    const result = winResult;

    // Year range
    let minYear = 9999, maxYear = 0;
    const genres = new Set();
    for (const nodeId of (result.treeNodeIds || [])) {
      const data = gameController.board?.getNodeData(nodeId);
      if (data?.year) {
        const y = parseInt(data.year, 10);
        if (y < minYear) minYear = y;
        if (y > maxYear) maxYear = y;
      }
      if (data?.genres) {
        for (const g of data.genres) genres.add(g);
      }
    }

    // Director's Cut
    let directorsCut = null;
    try {
      directorsCut = gameController.getDirectorsCut();
    } catch {}

    return {
      yearRange: minYear < 9999 ? `${minYear}–${maxYear}` : null,
      genreCount: genres.size,
      directorsCut: directorsCut?.uniqueMediaCount ?? null,
      boardNodes: gameController.board?.getNodeIds().length || 0,
    };
  }, [winResult, gameController]);

  const handleShare = async () => {
    const names = targets.map(t => t._resolvedName || t.name || '').filter(Boolean);
    const nameStr = names.length === 2
      ? `${names[0]} \u2192 ${names[1]}`
      : names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];

    let text = `Connect the Stars\n\n`;
    text += `${nameStr}\n`;
    text += `${filmCount} connecting film${filmCount !== 1 ? 's' : ''}\n`;
    if (stats.hintsUsed) text += `${stats.hintsUsed} hint${stats.hintsUsed !== 1 ? 's' : ''}\n`;
    text += '\n' + getShareUrl(targets);

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback
    }
  };

  const handleSaveResult = () => {
    recordGame({
      targets: targets.map(t => ({ id: t.id, type: t.type, name: t._resolvedName || t.name })),
      won: true,
      score: filmCount,
      hintsUsed: stats.hintsUsed,
      pathString: winResult.pathString,
      pathNodeIds: winResult.treeNodeIds ? [...winResult.treeNodeIds] : [],
    });
  };

  // Auto-save on first win
  React.useEffect(() => {
    if (firstWin) handleSaveResult();
  }, [firstWin]);

  return (
    <div className={`win-panel win-panel--${winPanelState}`}>
      {/* Collapsed bar */}
      <button
        className="win-panel__bar"
        onClick={() => setWinPanelState(
          winPanelState === 'closed' ? 'peeked' :
          winPanelState === 'peeked' ? 'expanded' : 'closed'
        )}
      >
        <span className="win-panel__bar-text">
          {isNewBest && winPanelState === 'closed' ? 'New best! ' : ''}
          Best: {filmCount} film{filmCount !== 1 ? 's' : ''}
          {stats.hintsUsed > 0 ? ` \u00B7 ${stats.hintsUsed} hint${stats.hintsUsed !== 1 ? 's' : ''}` : ''}
        </span>
        <span className="win-panel__bar-chevron">
          {winPanelState === 'expanded' ? '\u25BC' : '\u25B2'}
        </span>
      </button>

      {/* Peeked: path strip */}
      {(winPanelState === 'peeked' || winPanelState === 'expanded') && (
        <div className="win-panel__path">
          <div className="win-panel__names">
            {targets.length === 2
              ? `${targets[0]._resolvedName || targets[0].name || ''} \u2192 ${targets[1]._resolvedName || targets[1].name || ''}`
              : targets.map(t => t._resolvedName || t.name || '').join(' & ')
            }
          </div>
          <PathVisualization result={winResult} controller={gameController} />
        </div>
      )}

      {/* Expanded: insights + share */}
      {winPanelState === 'expanded' && insights && (
        <div className="win-panel__insights">
          <h3 className="win-panel__insights-title">That's a wrap!</h3>

          <div className="win-panel__insights-grid">
            <div className="win-panel__insight">
              <span className="win-panel__insight-value">{filmCount}</span>
              <span className="win-panel__insight-label">Films in path</span>
            </div>
            {insights.yearRange && (
              <div className="win-panel__insight">
                <span className="win-panel__insight-value">{insights.yearRange}</span>
                <span className="win-panel__insight-label">Year range</span>
              </div>
            )}
            {insights.genreCount > 0 && (
              <div className="win-panel__insight">
                <span className="win-panel__insight-value">{insights.genreCount}</span>
                <span className="win-panel__insight-label">Genres</span>
              </div>
            )}
            {insights.directorsCut !== null && insights.directorsCut !== filmCount && (
              <div className="win-panel__insight win-panel__insight--cut">
                <span className="win-panel__insight-value">{insights.directorsCut}</span>
                <span className="win-panel__insight-label">Director's Cut</span>
              </div>
            )}
            <div className="win-panel__insight">
              <span className="win-panel__insight-value">{insights.boardNodes}</span>
              <span className="win-panel__insight-label">Board nodes</span>
            </div>
          </div>

          <div className="win-panel__actions">
            <button className="win-panel__share" onClick={handleShare}>
              Copy Share Text
            </button>
            <button className="win-panel__action" onClick={() => setWinPanelState('closed')}>
              Continue Playing
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
